/**
 * mentorNudge.ts
 * Pure, presence-based mentor detection for the "Staff this job" refine popover
 * (PHASE2-REFRAME.md §5 — the gentle mentor prompt).
 *
 * Mirrors the backend skill gate (skillGateHelper + evaluateMentorRequirement):
 * a person qualifies as a mentor at skill level ≥ MENTOR_SKILL_LEVEL (L3+); a
 * junior (L1/L2) on a task with NO L3+ present that week needs a mentor. The
 * nudge is ADVISORY only — it never blocks a write (matching the warn-only gate
 * the write endpoints already apply).
 *
 * No React / MUI — the components render the result. Testable directly.
 */

import { MENTOR_SKILL_LEVEL } from './rosterAllocation';

/** A recorded skill level (1–5) or null/undefined when the person is unrated. */
export type SkillLevel = number | null | undefined;

/** L3+ — self-sufficient and able to supervise a junior. */
export function isMentorSkill(level: SkillLevel): boolean {
  return typeof level === 'number' && level >= MENTOR_SKILL_LEVEL;
}

/**
 * A hands-on junior (L1/L2) who needs an L3+ mentor present. Unrated (null) is
 * NOT treated as a junior here — the nudge deliberately fires only for a known
 * L1/L2, so it never nags about people with no skill record (the write-time
 * gate still applies server-side). Matches the plan wording "L1/L2".
 */
export function isJuniorSkill(level: SkillLevel): boolean {
  return typeof level === 'number' && level > 0 && level < MENTOR_SKILL_LEVEL;
}

/**
 * Given the skill levels of everyone PRESENT on a task that week, does the task
 * need a mentor? True iff there is at least one L1/L2 junior and no L3+ mentor.
 *
 * @param presentLevels  Levels of the people on the task this week.
 * @param skillRequired  When false (the task type requires no skill), the gate
 *                       never applies → always false. Defaults to true.
 */
export function taskNeedsMentor(
  presentLevels: readonly SkillLevel[],
  skillRequired = true,
): boolean {
  if (!skillRequired) return false;
  const hasMentor = presentLevels.some(isMentorSkill);
  if (hasMentor) return false;
  return presentLevels.some(isJuniorSkill);
}
